import type { AudioService } from "../audio/ports/AudioService";
import type { HapticsService } from "../haptics/HapticsService";
import type { SpinSpeedMode } from "./SpinCoordinatorTypes";
import type { Log, PresentationClock, PresentationTaskRunner } from "@slot/presentation";

/** The mechanic's slam scopes by role (`MechanicPlugin.slamScopes`, decision 23). */
export interface SlamScopes {
  /** The pre-spin motion's scope: finished by a slam, never re-armed after it (the arm would leak into the next sub-round's drop-out). */
  preSpin?: string;
  /** The round's motion scopes: finished by a slam, then re-armed for the async gap before the reveal starts. */
  presentation: readonly string[];
}

export interface SpinMotionControlDeps {
  runner: PresentationTaskRunner;
  clock: PresentationClock;
  audioService: AudioService;
  hapticsService: HapticsService;
  /**
   * The mechanic's own slam: runs first, before the shell force-finishes its scopes, so a mechanic
   * driving its motion under its own scope brakes instead of snapping. Returns true when it had
   * motion to stop. Absent for a mechanic that lives in the shell scopes alone (the cascade).
   */
  mechanicSlamStop?: () => boolean;
  /** The mechanic's scopes: armed to finish instantly in a turbo mode, force-finished on a slam. */
  slamScopes: SlamScopes;
  /** The game's pre-spin animation (the drop-out); it releases the "spin" barrier when the grid may reveal. */
  preSpinDropOut?: () => Promise<void>;
  log: Log;
}

/** The presentation side of a spin: the speed mode on the clocks, the slam arming per spin, the slam itself. */
export class SpinMotionControl {
  /** Every slam scope, the pre-spin one first. */
  private readonly allScopes: readonly string[];

  public constructor(private readonly deps: SpinMotionControlDeps) {
    const { preSpin, presentation } = deps.slamScopes;
    this.allScopes = preSpin ? [preSpin, ...presentation] : presentation;
  }

  /** Before the request: a turbo mode arms the mechanic's scopes to finish instantly; then the game's drop-out. */
  public async preSpin(): Promise<void> {
    const runner = this.deps.runner;
    // Stale arms from a prior spin (a rejected spin in turbo) never leak into this one.
    for (const scope of this.allScopes) runner.disarmSlamNextRun(scope);
    const mode = this.deps.clock.getMode();
    if (mode === "turbo" || mode === "super_turbo") {
      for (const scope of this.allScopes) runner.armSlamNextRun(scope);
    }
    await this.deps.preSpinDropOut?.();
  }

  /**
   * The player's slam: the mechanic brakes, its scopes finish, and the presentation scopes stay armed
   * for the async gap before the reveal starts — a pre-spin arm would leak into the next sub-round's
   * drop-out.
   */
  public slamStop(): void {
    const runner = this.deps.runner;
    const handledByMechanic = this.deps.mechanicSlamStop?.() ?? false;
    let slammedScope = false;
    for (const scope of this.allScopes) slammedScope = runner.slamFinishScope(scope) || slammedScope;
    for (const scope of this.deps.slamScopes.presentation) runner.armSlamNextRun(scope);
    this.deps.audioService.control.notifySlam();
    // The cue plays only when something was in flight: a tap after the motion completed is silent.
    if (handledByMechanic || slammedScope) {
      this.deps.audioService.presentation.emit({ id: "spin.slam" });
      this.deps.hapticsService.play("soft");
    }
  }

  public setSpeedMode(mode: SpinSpeedMode): void {
    this.deps.log(`[SpinMotion] speed mode=${mode}`);
    this.deps.clock.setMode(mode);
    this.deps.audioService.control.setSpeedMode(mode === "super_turbo" ? "super" : mode);
  }
}
